import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Check, Loader2, IdCard } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { SelfieCapture } from "@/components/kyc/SelfieCapture";
import { KycBanner, KycDetailsForm } from "@/components/kyc/KycSections";

const DOCS = [
  { key: "dl_front", label: "Driving licence — front" },
  { key: "dl_back", label: "Driving licence — back" },
  { key: "aadhaar_front", label: "Aadhaar — front" },
  { key: "aadhaar_back", label: "Aadhaar — back" },
];

function DocSlot({ doc, uploaded, onFile, disabled }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    await onFile(doc.key, f);
    setBusy(false);
  };
  return (
    <label className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-4 transition-colors duration-200 ${uploaded ? "border-emerald-200 bg-emerald-50" : "border-dashed border-black/15 bg-white hover:border-[#FF5A00]"} ${disabled ? "pointer-events-none opacity-60" : ""}`} data-testid={`kyc-slot-${doc.key}`}>
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${uploaded ? "bg-emerald-500 text-white" : "bg-neutral-100 text-neutral-500"}`}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : uploaded ? <Check className="h-5 w-5" /> : <Upload className="h-5 w-5" />}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{doc.label}</span>
        <span className="block text-xs text-neutral-500">{disabled && uploaded ? "Under review" : uploaded ? "Uploaded · tap to replace" : "JPG, PNG or PDF · max 8 MB"}</span>
      </span>
      <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={pick} data-testid={`kyc-input-${doc.key}`} />
    </label>
  );
}

export function KycPanel({ onStatus }) {
  const api = useApi();
  const [kyc, setKyc] = useState(null);
  const [dl, setDl] = useState("");
  const [a4, setA4] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");

  const apply = useCallback((k) => {
    setKyc(k);
    onStatus?.(k.status);
  }, [onStatus]);

  useEffect(() => {
    api.get("/kyc/me").then((r) => {
      apply(r.data);
      setDl(r.data.dl_number);
      setA4(r.data.aadhaar_last4);
    }).catch((e) => setLoadError(errMsg(e)));
  }, [api, apply]);

  const upload = async (docType, file) => {
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("file", file);
    try {
      const r = await api.post("/kyc/upload", fd);
      apply(r.data);
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/kyc/submit", { dl_number: dl, aadhaar_last4: a4 });
      apply(r.data);
      toast.success("Documents submitted for verification");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (loadError) return <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700" data-testid="kyc-load-error">Couldn't load your verification status: {loadError}. Please refresh the page.</div>;
  if (!kyc) return <div className="rounded-3xl border border-black/5 bg-white p-6 text-sm text-neutral-500">Loading verification…</div>;
  const locked = kyc.status === "APPROVED";
  const editable = ["NOT_SUBMITTED", "REJECTED"].includes(kyc.status);
  const allDocs = Object.values(kyc.docs).every(Boolean);

  return (
    <div className="space-y-5 rounded-3xl border border-black/5 bg-white p-6 sm:p-8" data-testid="kyc-panel">
      <div className="flex items-center gap-3">
        <IdCard className="h-6 w-6 text-[#FF5A00]" />
        <h2 className="text-xl font-semibold">Driver verification</h2>
      </div>
      <KycBanner kyc={kyc} />
      {!locked && (
        <>
          <p className="text-sm text-neutral-600">Upload once and we'll keep it on file for future trips. Please mask the first 8 digits of your Aadhaar number (or upload a masked Aadhaar from UIDAI).</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {DOCS.map((d) => <DocSlot key={d.key} doc={d} uploaded={kyc.docs[d.key]} onFile={upload} disabled={!editable} />)}
          </div>
          <SelfieCapture uploaded={kyc.docs.selfie} disabled={!editable} onCapture={(f) => upload("selfie", f)} />
          {editable && <KycDetailsForm dl={dl} setDl={setDl} a4={a4} setA4={setA4} allDocs={allDocs} busy={busy} onSubmit={submit} />}
        </>
      )}
    </div>
  );
}
